import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Reusable styled components for dashboard pages
 * These components use Tailwind CSS classes for consistent styling
 *
 * Migration: MUI styled() → React components with Tailwind
 */

// Main dashboard card component with consistent elevation and borders
export const DashboardCard = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow-sm h-full transition-all duration-300 hover:shadow-md",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
DashboardCard.displayName = 'DashboardCard';

// Dashboard container with consistent background
export const DashboardContainer = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex-grow py-6 px-4 sm:px-6 lg:px-8 bg-muted/50 min-h-screen max-w-7xl mx-auto w-full",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
DashboardContainer.displayName = 'DashboardContainer';

// Stats card with enhanced styling
export const StatsCard = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow-sm h-full transition-all duration-300",
      "hover:shadow-lg hover:-translate-y-0.5",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
StatsCard.displayName = 'StatsCard';

// Quick action item with consistent hover effects
export const QuickActionItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center p-4 bg-muted rounded-lg cursor-pointer transition-all duration-300",
      "hover:bg-muted/80 hover:translate-x-1",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
QuickActionItem.displayName = 'QuickActionItem';

// Role-specific content container
export const RoleContentContainer = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("mb-8", className)}
    {...props}
  >
    {children}
  </div>
));
RoleContentContainer.displayName = 'RoleContentContainer';

// Icon wrapper with consistent styling
export const IconWrapper = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg p-2 flex items-center justify-center bg-primary/10",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
IconWrapper.displayName = 'IconWrapper';

// Additional utility components for dashboard layouts

// Card header section
export const CardHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  >
    {children}
  </div>
));
CardHeader.displayName = 'CardHeader';

// Card content section
export const CardContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("p-6 pt-0", className)}
    {...props}
  >
    {children}
  </div>
));
CardContent.displayName = 'CardContent';

// Card title
export const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  >
    {children}
  </h3>
));
CardTitle.displayName = 'CardTitle';

// Grid container for dashboard cards
export const DashboardGrid = React.forwardRef(({ className, children, columns = 4, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "grid gap-4 sm:gap-6",
      columns === 2 && "grid-cols-1 sm:grid-cols-2",
      columns === 3 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
      columns === 4 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
DashboardGrid.displayName = 'DashboardGrid';
